import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { VideoOffIcon } from "lucide-react";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";

import { DotPulse } from "./DotPulse";

const VideoDisplayer = styled.div`
  display: flex;
  align-items: center;
  width: auto;
  text-align: left;
  overflow: hidden;

  .video-embedded iframe,
  .video-uploaded video {
    position: relative;
    max-height: 350px;
    transition: max-height 0.5s;
    -webkit-transition: max-height 0.5s;
  }

  &.can-expand {
    .video-embedded iframe:hover,
    .video-uploaded video:hover {
      max-height: min(80vh, 600px);
    }
  }

  .video-uploaded,
  .video-embedded {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;

    max-width: 100%;

    video,
    iframe {
      max-width: 100%;
    }
  }
`;

const VideoStatus = styled.div<{ $accent: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 620px;
  max-width: 100%;
  margin: 0 auto;
  aspect-ratio: 16 / 9;
  max-height: 350px;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 8px;
  background-color: #f4f5f7;
  color: rgba(0, 0, 0, 0.55);
  text-align: center;

  .video-status-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    margin-bottom: 12px;
    border-radius: 50%;
    background-color: #fff;
    color: ${({ $accent }) => $accent};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .video-status-header {
    font-size: 1.07em;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.87);
  }

  .video-status-body {
    margin: 0;
    font-size: 0.93em;
  }
`;

type VideoPlayerProps = {
  src: string;
  force?: boolean;
  canExpand?: boolean;
  style?: React.CSSProperties;
};

const VideoEmbed = ({ src }: { src: string }) => {
  return (
    <div className="video-embedded">
      <iframe
        title="Video"
        style={{ height: 350, width: "100%" }}
        src={src.replace("embed-", "")}
        frameBorder="0"
        allow="autoplay; encrypted-media"
        allowFullScreen
      ></iframe>
    </div>
  );
};

const VideoUploaded = ({ src }: { src: string }) => {
  const [headersLoading, setHeadersLoading] = useState(true);
  const [videoHeaders, setVideoHeaders] = useState<Record<string, string>>();
  const [devOverride, setDevOverride] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();

  const isConverted =
    videoHeaders?.["x-amz-meta-video-converted"] === "true" ||
    !!videoHeaders?.["x-amz-meta-mediaconvert-jobid"];

  useEffect(() => {
    setHeadersLoading(true);
    let isMounted = true;

    let isDone = false;
    const fetchHeaders = () => {
      fetch(src, { method: "HEAD" })
        .then((response) => {
          if (!isMounted) return;

          const headers: Record<string, string> = {};
          response.headers.forEach((value, key) => {
            headers[key] = value;
          });
          setVideoHeaders(headers);
          setHeadersLoading(false);

          if (
            headers["x-amz-meta-video-converted"] === "true" ||
            !!headers["x-amz-meta-mediaconvert-jobid"]
          ) {
            isDone = true;
          }
        })
        .catch((error) => {
          if (!isMounted) return;
          setHeadersLoading(false);
          console.error("Error fetching video:", error);
        });
    };
    fetchHeaders();

    if (!isDone) {
      const interval = setInterval(() => {
        fetchHeaders();
        if (isDone) {
          clearInterval(interval);
        }
      }, 3000);

      const stopTimeout = setTimeout(
        () => {
          clearInterval(interval);
        },
        60 * 1000 * 5,
      );
      return () => {
        clearInterval(interval);
        clearTimeout(stopTimeout);
        isMounted = false;
      };
    }

    return () => {
      isMounted = false;
    };
  }, [src]);

  const canPlay = useMemo(() => {
    if (!src) return false;
    const v = src.toLowerCase();
    let type = "video/";
    if (v.indexOf(".mp4") > 0) type += "mp4";
    else if (v.indexOf(".avi") > 0) type += "avi";
    else type += "mp4";
    const videoElement = document.createElement("video");
    const canPlay = !!videoElement.canPlayType(type);
    videoElement.remove();
    return canPlay;
  }, [src]);

  if (headersLoading) {
    return (
      <VideoStatus $accent={theme.primaryColour}>
        <DotPulse size={12} />
      </VideoStatus>
    );
  }

  if (!isConverted && !devOverride) {
    return (
      <VideoStatus $accent={theme.primaryColour}>
        <DotPulse size={12} style={{ marginBottom: 12 }} />
        <div className="video-status-header">
          {t("videoPlayer.processing.header")}
        </div>
        <p className="video-status-body">{t("videoPlayer.processing.body")}</p>
        {import.meta.env.VITE_STAGE === "dev" ? (
          <Button
            size="small"
            variant="ghost"
            style={{ marginTop: 8 }}
            onClick={() => setDevOverride(true)}
          >
            Dev: force original source
          </Button>
        ) : null}
      </VideoStatus>
    );
  }

  if (!canPlay) {
    return (
      <VideoStatus $accent={theme.primaryColour}>
        <div className="video-status-icon">
          <VideoOffIcon size={24} />
        </div>
        <div className="video-status-header">
          {t("videoPlayer.unsupported.header")}
        </div>
        <p className="video-status-body">{t("videoPlayer.unsupported.body")}</p>
      </VideoStatus>
    );
  }

  return (
    <div className="video-uploaded">
      <video key={src} controls preload="metadata">
        <source src={src} type="video/mp4" />
        Your browser does not support this video.
      </video>
    </div>
  );
};

export const VideoPlayer = ({ src, canExpand, style }: VideoPlayerProps) => {
  if (!src) return null;
  return (
    <VideoDisplayer className={cx(canExpand && "can-expand")} style={style}>
      {src.indexOf("embed-") === 0 ? (
        <VideoEmbed src={src} />
      ) : (
        <VideoUploaded src={src} />
      )}
    </VideoDisplayer>
  );
};
